import React, { useEffect, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import FormDataContext from "integrations/contexts/FormDataContext";
import { validateExternalIdRequiresArn } from "integrations/aws/utils/awsValidation";
import { GenericS3 } from "@graylog/enterprise-api";
import AWSAuthenticationTypes, { AWS_AUTH_TYPES } from "../common/aws/AWSAuthenticationTypes";
import { renderOptions } from "../common/Options";
import FormWrap from "../common/FormWrap";
import formValidation from "../common/utils/formValidation";
import ValidatedInput from "../common/ValidatedInput";
const StepAuthorize = ({ onSubmit, onChange }) => {
  const { t } = useTranslation("enterprise_integrations");
  const [regions, setRegions] = useState([]);
  useEffect(() => {
    GenericS3.getAWSRegions().then((response) => {
      const region = Object.keys(response).map((key) => ({ label: response[key], value: key }));
      setRegions(region);
    });
  }, []);
  const { formData } = useContext(FormDataContext);
  const [formError, setFormError] = useState(null);
  const { awsSecretKey } = formData;
  const [loading, setLoading] = useState(false);
  const handleSubmit = () => {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    const externalIdError = validateExternalIdRequiresArn(
      (_a = formData == null ? void 0 : formData.assumeRoleArn) == null ? void 0 : _a.value,
      (_b = formData == null ? void 0 : formData.awsExternalId) == null ? void 0 : _b.value
    );
    if (externalIdError) {
      setFormError({
        full_message: externalIdError,
        nice_message: externalIdError
      });
      return;
    }
    setLoading(true);
    GenericS3.checkCredentials({
      aws_s3_access_key: ((_c = formData == null ? void 0 : formData.awsAccessKey) == null ? void 0 : _c.value) || "",
      aws_s3_secret: ((_d = formData == null ? void 0 : formData.awsSecretKey) == null ? void 0 : _d.value) || "",
      sqs_queue_name: ((_e = formData == null ? void 0 : formData.sqsQueueName) == null ? void 0 : _e.value) || "",
      aws_s3_region: ((_f = formData == null ? void 0 : formData.awsS3Region) == null ? void 0 : _f.value) || "",
      assume_role_arn: ((_g = formData == null ? void 0 : formData.assumeRoleArn) == null ? void 0 : _g.value) || "",
      aws_external_id: ((_h = formData == null ? void 0 : formData.awsExternalId) == null ? void 0 : _h.value) || ""
    }).then((result) => {
      if (result.result === "valid") {
        setFormError(null);
        onSubmit();
      } else {
        setFormError({
          full_message: result.result,
          nice_message: t("genericS3.errors.unable_to_connect")
        });
      }
      setLoading(false);
    }).catch((err) => {
      setLoading(false);
      setFormError({
        full_message: err.message,
        nice_message: t("genericS3.errors.unable_to_connect")
      });
    });
  };
  const authType = formData.awsAuthenticationType && formData.awsAuthenticationType.value;
  const isFormValid = formValidation.isFormValid(
    [
      "genericS3Name",
      ...authType !== AWS_AUTH_TYPES.automatic ? ["awsAccessKey", "awsSecretKey"] : [],
      "awsS3Region"
    ],
    formData
  );
  return /* @__PURE__ */ React.createElement(
    FormWrap,
    {
      onSubmit: handleSubmit,
      buttonContent: t("common.buttons.verify_connection_and_proceed"),
      disabled: isFormValid,
      loading,
      title: "",
      error: formError,
      description: ""
    },
    /* @__PURE__ */ React.createElement(
      ValidatedInput,
      {
        className: "",
        id: "genericS3Name",
        type: "text",
        fieldData: formData.genericS3Name,
        onChange,
        placeholder: t("common.fields.input_name"),
        label: t("common.fields.input_name"),
        autoComplete: "off",
        help: t("common.fields.input_name_help"),
        defaultValue: awsSecretKey == null ? void 0 : awsSecretKey.value,
        required: true
      }
    ),
    /* @__PURE__ */ React.createElement(AWSAuthenticationTypes, { onChange }),
    /* @__PURE__ */ React.createElement(
      ValidatedInput,
      {
        className: "",
        id: "sqsQueueName",
        type: "text",
        onChange,
        fieldData: formData.sqsQueueName,
        label: t("genericS3.fields.sqs_queue_name"),
        help: t("genericS3.fields.sqs_queue_name_help"),
        required: true
      }
    ),
    /* @__PURE__ */ React.createElement(
      ValidatedInput,
      {
        type: "select",
        id: "awsS3Region",
        onChange,
        fieldData: formData.awsS3Region,
        help: t("genericS3.fields.s3_region_help"),
        required: true,
        label: t("genericS3.fields.s3_region")
      },
      renderOptions(regions, t("genericS3.fields.choose_region"), false)
    ),
    /* @__PURE__ */ React.createElement(
      ValidatedInput,
      {
        type: "text",
        id: "assumeRoleArn",
        fieldData: formData.assumeRoleArn,
        label: t("common.aws.assume_role_arn"),
        onChange,
        help: t("common.aws.assume_role_arn_help"),
        placeholder: "arn:aws:sts::123456789012:assumed-role/some-role",
        maxLength: 2048
      }
    ),
    /* @__PURE__ */ React.createElement(
      ValidatedInput,
      {
        type: "text",
        id: "awsExternalId",
        fieldData: formData.awsExternalId,
        label: t("common.aws.external_id"),
        onChange,
        help: t("common.aws.external_id_help"),
        placeholder: t("genericS3.fields.external_id_placeholder"),
        maxLength: 2048
      }
    )
  );
};
export default StepAuthorize;
